import { session } from './auth.svelte';
import { isKeycloakMode, refreshIfNeeded } from './keycloak';

export type Producto = 'clinica' | 'hospital';

export type TenantSummary = {
  producto: Producto;
  tenantId: string;
  nombre: string;
  plan: string | null;
  estado: string;
  edicion: string | null;
  trialEndsAt: string | null;
  creadoEn: string | null;
  esDemo: boolean;
  contactoEmail?: string | null;
  contactoTelefono?: string | null;
  contactoNombre?: string | null;
  tipoOrganizacion?: string | null;
  usuarioLogin: string;
};

export type TenantDetail = TenantSummary & {
  modulosActivos?: string[];
  config?: Record<string, unknown>;
  contacto?: { nombre?: string; email?: string; telefono?: string; cargo?: string };
};

export type ProvisionResult = {
  tenant: { tenantId: string; nombre: string };
  usuarioAdmin: string;
  tempPassword?: string;
  loginUrl: string;
  trialEndsAt: string;
};

export const PRODUCTO_LABEL: Record<Producto, string> = {
  clinica: 'Clínicas privadas',
  hospital: 'Hospital privado',
};

export const PRODUCTO_APP: Record<Producto, string> = {
  clinica: 'https://clinica-privada.com',
  hospital: 'https://app.toscana-hospital.com',
};

export function localProduct(): Producto {
  if (typeof location !== 'undefined' && location.hostname.includes('hospital')) return 'hospital';
  return 'clinica';
}

export function peerProduct(): Producto {
  return localProduct() === 'clinica' ? 'hospital' : 'clinica';
}

function apiBase(producto: Producto): string {
  if (producto === localProduct()) return '/api/auth';
  return `/api/admin-peer/${producto}`;
}

async function adminFetch<T>(producto: Producto, method: string, path: string, body?: Record<string, unknown>): Promise<T> {
  if (isKeycloakMode()) await refreshIfNeeded(30);
  const s = session.value;
  if (!s?.token) throw new Error('Debe iniciar sesión como equipo Toscana (admin-platform).');

  const headers: Record<string, string> = {
    Accept: 'application/json',
    Authorization: `Bearer ${s.token}`,
  };
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  const r = await fetch(`${apiBase(producto)}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (r.status === 403) throw new Error(`Acceso denegado en ${PRODUCTO_LABEL[producto]}.`);
  if (r.status === 401) throw new Error('Sesión expirada — vuelva a iniciar sesión.');
  if (!r.ok) {
    const txt = await r.text().catch(() => '');
    throw new Error(txt.slice(0, 200) || `${PRODUCTO_LABEL[producto]}: ${r.status}`);
  }
  return r.json() as Promise<T>;
}

type RawTenant = Omit<TenantSummary, 'producto'>;

function tag(producto: Producto, t: RawTenant): TenantSummary {
  return { ...t, producto };
}

export async function listTenants(producto: Producto): Promise<{ tenants: TenantSummary[]; total: number }> {
  const r = await adminFetch<{ tenants: RawTenant[]; total: number }>(producto, 'GET', '/v1/admin/tenants');
  return { total: r.total, tenants: r.tenants.map(t => tag(producto, t)) };
}

export async function listAllTenants(): Promise<{
  tenants: TenantSummary[];
  errors: Partial<Record<Producto, string>>;
}> {
  const local = localProduct();
  const remote = peerProduct();
  const errors: Partial<Record<Producto, string>> = {};
  const tenants: TenantSummary[] = [];

  const results = await Promise.allSettled([listTenants(local), listTenants(remote)]);
  for (let i = 0; i < results.length; i++) {
    const prod = i === 0 ? local : remote;
    const res = results[i];
    if (res.status === 'fulfilled') tenants.push(...res.value.tenants);
    else errors[prod] = res.reason instanceof Error ? res.reason.message : 'Error de conexión';
  }
  tenants.sort((a, b) => (b.creadoEn ?? '').localeCompare(a.creadoEn ?? ''));
  return { tenants, errors };
}

export async function getTenant(producto: Producto, tenantId: string): Promise<TenantDetail> {
  const d = await adminFetch<Record<string, unknown>>(producto, 'GET', `/v1/admin/tenants/${encodeURIComponent(tenantId)}`);
  return { ...(d as Omit<TenantDetail, 'producto'>), producto };
}

export async function patchTenant(producto: Producto, tenantId: string, patch: {
  nombre?: string;
  plan?: string;
  estado?: string;
  trialEndsAt?: string;
}): Promise<TenantDetail> {
  const d = await adminFetch<Record<string, unknown>>(producto, 'PATCH', `/v1/admin/tenants/${encodeURIComponent(tenantId)}`, patch);
  return { ...(d as Omit<TenantDetail, 'producto'>), producto };
}

export async function provisionTenant(producto: Producto, payload: {
  nombre: string;
  email: string;
  telefono: string;
  plan: string;
  tipoOrganizacion: string;
  slugSugerido?: string;
  cargo?: string;
  whatsapp?: string;
  diasTrial?: number;
}): Promise<ProvisionResult> {
  return adminFetch(producto, 'POST', '/v1/admin/tenants/provision', payload);
}

export function isPlatformAdminRole(roles: string[] | undefined): boolean {
  return (roles ?? []).includes('admin-platform');
}
